import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

// Easing function type for animations - defined here as the source of truth
export type EasingFunction = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

export type BodyPart =
  | 'head'
  | 'body'
  | 'leftArm'
  | 'rightArm'
  | 'leftLeg'
  | 'rightLeg'
  | 'control';

/** Rotation axis for tool movement */
export type RotationAxis = 'x' | 'y' | 'z';

export type ToolType = 'prebuilt';

/**
 * HTTP endpoints for hardware control (optional)
 */
export interface ToolHttpEndpoints {
  start?: string;
  stop?: string;
}

/**
 * Tool definition - defines what a tool is and its default properties
 *
 * Tools are body-part-specific and define the default easing and duration
 * for animations. Each tool maps to exactly one body part.
 */
export interface ToolDefinition {
  id: string;
  name: string;
  bodyPart: BodyPart;
  type: ToolType;
  defaultDuration: number;
  defaultEasing: EasingFunction;
  /** Minimum rotation angle in degrees */
  minDegrees: number;
  /** Maximum rotation angle in degrees */
  maxDegrees: number;
  /** Docking position in degrees (rest/transport state) */
  dockingPosition: number;
  /** Starting position in degrees (initial animation state) */
  startingPosition: number;
  /** Color for tool display (hex or CSS variable) */
  color: string;
  /** Whether this is a prebuilt system tool (cannot be deleted) */
  isPrebuilt: boolean;
  /** Optional icon identifier for the tool */
  icon?: string;
  /** Optional HTTP endpoints for hardware control */
  httpEndpoints?: ToolHttpEndpoints;
  /** Rotation axis for movement (x = front-back, y = left-right, z = top-bottom) */
  axis: RotationAxis;
}

/**
 * Generate a unique tool ID
 * Pattern: tool-{timestamp}-{random}
 */
export const generateToolId = (): string => {
  return `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

interface ToolState {
  preBuiltTools: ToolDefinition[];
  /** Global control tools (Wait, Stop) - shared across all puppets */
  controlTools: ToolDefinition[];
}

interface ToolActions {
  getToolById: (id: string) => ToolDefinition | undefined;
  /** Get global control tools */
  getControlTools: () => ToolDefinition[];
}

type ToolStore = ToolState & ToolActions;

/**
 * Create the 6 prebuilt body part tools (puppet-specific)
 *
 * 6 body part tools (one per motor) for string puppet
 * Each body part tool uses ease-in-out for smooth animation
 * These tools belong to puppets and can be customized per puppet.
 *
 * Hardware-specific degree ranges (string puppet):
 * - Head: 0-90° (0 = nodded down, 90 = straight/up)
 * - Body: 0-180° (0 = turned left, 90 = center, 180 = turned right)
 * - Arms: 0-180° (0 = down, 90 = middle, 180 = up)
 * - Legs: 0-90° (0 = down, 90 = knee bent) - string attached to knee joint
 *
 * All docking and starting positions default to 0
 */
export const createPreBuiltBodyPartTools = (): ToolDefinition[] => {
  return [
    {
      id: 'prebuilt-head',
      name: 'Head',
      bodyPart: 'head',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 90,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-head)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-body',
      name: 'Body',
      bodyPart: 'body',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 180,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-body)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-leftArm',
      name: 'Left Arm',
      bodyPart: 'leftArm',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 180,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-arm)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-rightArm',
      name: 'Right Arm',
      bodyPart: 'rightArm',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 180,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-arm)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-leftLeg',
      name: 'Left Leg',
      bodyPart: 'leftLeg',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 90,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-leg)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-rightLeg',
      name: 'Right Leg',
      bodyPart: 'rightLeg',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 90,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-leg)',
      isPrebuilt: true,
      axis: 'x',
    },
  ];
};

/**
 * Create global control tools (not puppet-specific)
 *
 * Control tools (Wait, Stop, Reset Position) are global - they control
 * timeline flow, not puppet body parts. These are shared across all puppets.
 */
export const createControlTools = (): ToolDefinition[] => {
  return [
    {
      id: 'prebuilt-control-wait',
      name: 'Wait',
      bodyPart: 'control',
      type: 'prebuilt',
      defaultDuration: 1.0,
      defaultEasing: 'linear',
      minDegrees: 0,
      maxDegrees: 0,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-control)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-control-stop',
      name: 'Stop',
      bodyPart: 'control',
      type: 'prebuilt',
      defaultDuration: 0.1,
      defaultEasing: 'linear',
      minDegrees: 0,
      maxDegrees: 0,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-control)',
      isPrebuilt: true,
      axis: 'x',
    },
    {
      id: 'prebuilt-control-reset-position',
      name: 'Reset Position',
      bodyPart: 'control',
      type: 'prebuilt',
      // Duration is calculated dynamically based on movement speed
      // Default of 1.0 is a placeholder - actual duration set when tool is added
      defaultDuration: 1.0,
      defaultEasing: 'ease-in-out',
      minDegrees: 0,
      maxDegrees: 0,
      dockingPosition: 0,
      startingPosition: 0,
      color: 'var(--color-body-control)',
      isPrebuilt: true,
      axis: 'x',
    },
  ];
};

/**
 * Create all prebuilt tools (body part + control)
 * @deprecated Use createPreBuiltBodyPartTools() for puppet tools
 * and createControlTools() for global control tools
 */
export const createPreBuiltTools = (): ToolDefinition[] => {
  return [...createPreBuiltBodyPartTools(), ...createControlTools()];
};

// Validation helper for body parts
export const isValidBodyPart = (value: unknown): value is BodyPart => {
  const validBodyParts: BodyPart[] = [
    'head',
    'body',
    'leftArm',
    'rightArm',
    'leftLeg',
    'rightLeg',
    'control',
  ];
  return (
    typeof value === 'string' && validBodyParts.includes(value as BodyPart)
  );
};

export const useToolStore = create<ToolStore>()(
  immer((_, get) => ({
    // Initial state - prebuilt tools for backwards compatibility
    preBuiltTools: createPreBuiltTools(),
    // Global control tools (Wait, Stop) - shared across all puppets
    controlTools: createControlTools(),

    // Actions
    getToolById: (id) => {
      const state = get();
      // Check both prebuilt tools and control tools
      return (
        state.preBuiltTools.find((tool) => tool.id === id) ||
        state.controlTools.find((tool) => tool.id === id)
      );
    },

    getControlTools: () => {
      return get().controlTools;
    },
  }))
);
